'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, Copy, Check, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface SignInFormData {
  email: string;
  password: string;
  remember: boolean;
}

const demoCredentials = [
  {
    id: 'cred-company',
    role: 'Company Admin',
    email: 'carlos@citycabs.co.uk',
    password: 'CabsAdmin#2026',
    href: '/company-admin-dashboard',
  },
];

const masterCredential = {
  email: 'marcus@rideos.io',
  password: 'RideOS#Master2026',
  href: '/master-admin-dashboard',
};

export default function SignInForm() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showMasterAccess, setShowMasterAccess] = useState(false);
  const [masterCode, setMasterCode] = useState('');
  const [masterCodeError, setMasterCodeError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
  } = useForm<SignInFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const handleCopy = (text: string, fieldId: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const autofillCredential = (cred: (typeof demoCredentials)[0]) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
  };

  const handleMasterAccess = () => {
    if (masterCode === 'RIDEOS-MASTER') {
      setValue('email', masterCredential.email);
      setValue('password', masterCredential.password);
      setShowMasterAccess(false);
      setMasterCode('');
      setMasterCodeError('');
      toast.success('Master Admin credentials loaded.');
    } else {
      setMasterCodeError('Invalid access code.');
    }
  };

  const onSubmit = async (data: SignInFormData) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);

      // Determine redirect based on email
      if (data.email === masterCredential.email) {
        toast.success('Welcome, Master Admin!');
        router.push(masterCredential.href);
      } else {
        const match = demoCredentials.find((c) => c.email === data.email);
        toast.success('Welcome back! Redirecting to your dashboard...');
        router.push(match?.href ?? '/company-admin-dashboard');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Invalid credentials';
      if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('credentials')) {
        setError('email', { message: 'Invalid email or password. Use the demo credentials below.' });
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fade-in">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className="label-field">Email Address</label>
          <input
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
            placeholder="you@company.com"
            className={`input-field ${errors.email ? 'error' : ''}`}
            autoComplete="email"
          />
          {errors.email && (
            <p className="text-xs mt-1.5" style={{ color: 'var(--danger)' }}>
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="label-field mb-0">Password</label>
            <a href="#" className="text-xs transition-colors hover:underline" style={{ color: 'var(--primary)' }}>
              Forgot password?
            </a>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
              placeholder="••••••••"
              className={`input-field pr-10 ${errors.password ? 'error' : ''}`}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 transition-colors"
              style={{ color: 'var(--muted-foreground)' }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs mt-1.5" style={{ color: 'var(--danger)' }}>
              {errors.password.message}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2.5">
          <input id="remember" type="checkbox" {...register('remember')} className="w-4 h-4 rounded accent-amber-500" />
          <label htmlFor="remember" className="text-sm cursor-pointer" style={{ color: 'var(--muted-foreground)' }}>
            Keep me signed in for 30 days
          </label>
        </div>

        <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-sm font-semibold" style={{ minHeight: '44px' }}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In'}
        </button>
      </form>

      <div
        className="mt-8 rounded-xl overflow-hidden"
        style={{ border: '1px solid rgba(245,158,11,0.2)', background: 'rgba(245,158,11,0.04)' }}
      >
        <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(245,158,11,0.15)' }}>
          <p className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>
            Demo Credentials — click to autofill
          </p>
        </div>
        <div className="divide-y" style={{ borderColor: 'rgba(245,158,11,0.1)' }}>
          {demoCredentials.map((cred) => (
            <div
              key={cred.id}
              className="px-4 py-3 hover:bg-amber-500/5 cursor-pointer transition-colors group"
              onClick={() => autofillCredential(cred)}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>{cred.role}</span>
                <span className="text-2xs opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--primary)' }}>
                  Click to autofill
                </span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs" style={{ color: 'var(--muted-foreground)' }}>{cred.email}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleCopy(cred.email, `${cred.id}-email`); }}
                    className="p-1 rounded transition-colors hover:bg-muted"
                    aria-label="Copy email"
                  >
                    {copiedField === `${cred.id}-email` ? (
                      <Check className="w-3 h-3" style={{ color: 'var(--success)' }} />
                    ) : (
                      <Copy className="w-3 h-3" style={{ color: 'var(--muted-foreground)' }} />
                    )}
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs" style={{ color: 'var(--muted-foreground)' }}>{cred.password}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleCopy(cred.password, `${cred.id}-pass`); }}
                    className="p-1 rounded transition-colors hover:bg-muted"
                    aria-label="Copy password"
                  >
                    {copiedField === `${cred.id}-pass` ? (
                      <Check className="w-3 h-3" style={{ color: 'var(--success)' }} />
                    ) : (
                      <Copy className="w-3 h-3" style={{ color: 'var(--muted-foreground)' }} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => setShowMasterAccess(!showMasterAccess)}
          className="text-xs transition-colors hover:underline flex items-center gap-1 mx-auto"
          style={{ color: 'var(--muted-foreground)', opacity: 0.4 }}
          aria-label="Platform administrator access"
        >
          <ShieldAlert className="w-3 h-3" />
          Platform Admin
        </button>
      </div>

      {showMasterAccess && (
        <div
          className="mt-3 p-4 rounded-xl slide-up"
          style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        >
          <p className="text-xs font-semibold mb-3" style={{ color: 'var(--foreground)' }}>
            Enter platform access code
          </p>
          <div className="flex gap-2">
            <input
              type="password"
              value={masterCode}
              onChange={(e) => { setMasterCode(e.target.value); setMasterCodeError(''); }}
              placeholder="Access code"
              className="input-field flex-1 py-2 text-xs"
              onKeyDown={(e) => e.key === 'Enter' && handleMasterAccess()}
            />
            <button type="button" onClick={handleMasterAccess} className="btn-primary px-3 py-2 text-xs">
              Enter
            </button>
          </div>
          {masterCodeError && (
            <p className="text-xs mt-2" style={{ color: 'var(--danger)' }}>{masterCodeError}</p>
          )}
        </div>
      )}
    </div>
  );
}